import { createTheme } from '@mui/material';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// MUI type augmentations this theme relies on (Palette.accent/white,
// TypographyVariants.caption, IconButton/TextField color overrides) are
// declared in Z1Theme.ts and consumed here via the shared `@coverright/ui/themes`
// barrel — this workspace's TS setup does not reliably merge `declare module`
// augmentations of the same interface across multiple files (see AppTheme.ts's
// ButtonPropsVariantOverrides comment), so they are intentionally not
// re-declared here to avoid a second, competing (and losing) declaration site.

/**
 * Medicare AI theme — shared theme for apps/ai-consumer, apps/compliance and
 * apps/aetna-marketplace. Ported 1:1 from apps/ai-consumer's former local
 * `aiConsumerTheme` (apps/ai-consumer/src/app/theme.ts) — same palette,
 * typography and component overrides, only the font-loading strategy
 * changed (fontsource package instead of local /assets/fonts woff2 files,
 * which only existed inside apps/ai-consumer's own build).
 *
 * Design tokens used throughout plans/advanced (e.g. the ad hoc `T` object in
 * apps/ai-consumer's comparison/ui/lib.ts) are NOT yet reconciled against this
 * theme's palette — that's a separate follow-up (see plan §3/Phase E).
 *
 * Key tokens:
 *   --primary:        #094451   brand primary (dark teal)
 *   --primary-hover:  #072F38   hover state for primary
 *   --accent-tint:    #E6F4F6   light cyan wash — hover / subtle surfaces
 *   --brand-cyan:     #1E95A0   bright cyan — brand accent
 *   --secondary:      oklch(0.95 0.0058 264.53) ≈ #ebebf5  light muted bg
 *   --muted:          #ececf0
 *   --muted-fg:       #717182
 *   --input-bg:       #f3f3f5
 *   --border:         rgba(0,0,0,0.1)
 *   --destructive:    #d4183d
 *   --radius:         0.625rem → 10px
 *     rounded-sm = 6px  (radius - 4px)
 *     rounded-md = 8px  (radius - 2px)  ← buttons & inputs
 *     rounded-lg = 10px (radius)         ← xl button, cards
 *     rounded-xl = 14px (radius + 4px)
 */
const primaryFont = ['Inter', 'sans-serif'].join(',');

export const medicareAiTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1648,
    },
  },

  shape: {
    borderRadius: 10, // --radius: 0.625rem
  },

  palette: {
    primary: {
      main: '#094451',      // --primary
      dark: '#072F38',      // --primary-hover
      light: '#E6F4F6',     // --accent-tint
      contrastText: '#ffffff',
    },
    secondary: {
      // Matches --secondary: oklch(0.95 0.0058 264.53) — light muted lavender
      // Used for "secondary" button variant: light bg + dark text
      main: '#ebebf5',
      dark: '#d8d8ed',
      light: '#f5f5fb',
      contrastText: '#111827',
    },
    error: {
      main: '#d4183d',      // --destructive
      light: '#fce4e9',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#b45309',      // --warning-text
      light: '#fffbeb',     // --warning-subtle
      contrastText: '#ffffff',
    },
    success: {
      main: '#15803d',      // --success-text
      light: '#f0fdf4',     // --success-subtle
      contrastText: '#ffffff',
    },
    info: {
      main: '#0369a1',      // --info-text
      light: '#f0f9ff',     // --info-subtle
      contrastText: '#ffffff',
    },
    white: {
      main: '#ffffff',
      dark: '#f8fafc',      // subtle hover
      light: '#ffffff',
      contrastText: '#094451',
    },
    background: {
      default: '#f9f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172b',
      secondary: '#45556c', // --muted-foreground
      disabled: '#9ea5b1',
    },
    divider: 'rgba(0, 0, 0, 0.1)', // --border
    grey: {
      '50':  '#f3f3f5',  // --input-background
      '100': '#ececf0',  // --muted
      '200': '#cbced4',  // --switch-background
      '300': '#9ea5b1',
      '400': '#717182',  // --muted-foreground
      '500': '#4b5563',
      '600': '#374151',
      '700': '#1f2937',
      '800': '#111827',  // --foreground
      '900': '#030712',
    },
  },

  typography: {
    fontFamily: primaryFont,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,

    h1: {
      fontSize: '1.5rem',     // --text-2xl = 24px
      fontWeight: 500,
      lineHeight: 1.25,
    },
    h2: {
      fontSize: '1.25rem',    // --text-xl = 20px
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.125rem',   // --text-lg = 18px
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1rem',       // --text-base = 16px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '0.875rem',   // --text-sm = 14px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.75rem',    // 12px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',       // --text-base
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',   // --text-sm
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
    },
  },

  components: {
    ResponsiveTable: {},

    MuiCssBaseline: {
      styleOverrides: `
        html, body, #root {
          height: 100%;
          font-size: 16px;
        }
        body {
          font-family: ${primaryFont};
          background-color: #F8FAFC;
          color: #0f172b;
          min-height: 100vh;
        }
      `,
    },

    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontFamily: primaryFont,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          cursor: 'pointer',
          transition: 'background-color 100ms, transform 100ms',
          '&:hover': { boxShadow: 'none' },
          '&:active': { transform: 'scale(0.97)' },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 2px #ffffff, 0 0 0 4px #094451', // ring-offset-2 ring-primary
          },
          '&:focus:not(:focus-visible)': {
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        },
        // sm  → h-[52px] / text-sm (14px) / px-4 / rounded-md (8px)
        sizeSmall: {
          fontSize: '16px',
          borderRadius: '8px',
          height: 48,
          padding: '0 16px',
          minWidth: 'unset',
        },
        // default/medium → h-14 (56px) / text-lg (18px) / px-5 / rounded-md (8px)
        sizeMedium: {
          fontSize: '1.125rem',
          borderRadius: '8px',
          height: 56,
          padding: '0 20px',
        },
        // large → xl from CVA: h-[72px] / text-lg (18px) / font-semibold (600) / px-8 / rounded-lg (10px)
        sizeLarge: {
          fontSize: '1.125rem',
          fontWeight: 600,
          borderRadius: '10px',
          height: 64,
          padding: '0 32px',
        },
      },
      variants: [
        // ── Primary ──────────────────────────────────────────────
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: '#094451',
            color: '#ffffff',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)', // shadow-md
            '&:hover': {
              backgroundColor: '#072F38',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
            },
            '&:active': { backgroundColor: 'rgba(7,47,56,0.9)' },
            '&.Mui-disabled': { backgroundColor: '#94a3b8', color: '#ffffff' }, // disabled:bg-slate-400
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            border: '1px solid #cbd5e1',   // border-slate-300
            color: '#374151',               // text-slate-700
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f8fafc',   // hover:bg-slate-50
              border: '1px solid #072F38',  // hover:border-primary-hover
            },
            '&.Mui-disabled': { border: '1px solid #cbced4', color: '#9ea5b1' },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: '#094451',
            '&:hover': { backgroundColor: '#F8FAFC' },
          },
        },
        // ── Secondary (light muted bg + dark text, matches --secondary token) ──
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: '#ebebf5',
            color: '#111827',
            '&:hover': { backgroundColor: '#d8d8ed' },
            '&.Mui-disabled': { backgroundColor: '#ececf0', color: '#9ea5b1' },
          },
        },
        // ── Brand cyan — use color="info" for teal CTA buttons ───
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            backgroundColor: '#1E95A0',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#1a7d87' },
            '&.Mui-disabled': { backgroundColor: '#ececf0', color: '#9ea5b1' },
          },
        },
        // ── Option (quiz radio button) ────────────────────────────
        // Unselected: white bg, slate-300 border; selected via aria-checked="true"
        {
          props: { variant: 'option' },
          style: {
            minHeight: 56,
            height: 'auto',
            padding: '0 16px',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: 500,
            justifyContent: 'space-between',
            textAlign: 'left',
            backgroundColor: '#ffffff',
            color: '#374151',
            border: '2px solid #cbd5e1',
            '&:hover': {
              backgroundColor: '#f8fafc', // bg-slate-50
              border: '2px solid #072F38', // hover:border-primary-hover
            },
            '&[aria-checked="true"]': {
              backgroundColor: '#094451', // bg-primary
              color: '#ffffff',
              border: '2px solid #094451',
              '&:hover': {
                backgroundColor: '#072F38',
                border: '2px solid #072F38',
              },
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: '0 0 0 2px #ffffff, 0 0 0 4px #094451', // ring-offset-2 ring-primary
            },
          },
        },
        // ── Destructive ───────────────────────────────────────────
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: '#d4183d',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#b01231' },
            '&.Mui-disabled': { backgroundColor: '#ececf0', color: '#9ea5b1' },
          },
        },
      ],
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          // Prevent layout shift on open
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
        },
        select: {
          // Vertically center text inside fixed-height input
          display: 'flex',
          alignItems: 'center',
          height: '100% !important',
          boxSizing: 'border-box',
          padding: '0 40px 0 16px !important',
        },
        icon: {
          top: '50%',
          transform: 'translateY(-50%)',
          right: 12,
          transition: 'transform 200ms ease',
          '&.MuiSelect-iconOpen': {
            transform: 'translateY(-50%) rotate(180deg)',
          },
        },
      },
    },

    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& .MuiTouchRipple-root *': { borderRadius: '8px !important' },
          '&:active': { transform: 'scale(0.97)' },
        },
        sizeSmall:  { padding: '6px',  width: 32, height: 32 },
        sizeMedium: { padding: '8px',  width: 40, height: 40 },
        sizeLarge:  { padding: '12px', width: 48, height: 48 },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: '10px',   // rounded-lg = --radius
          border: '1px solid rgba(0, 0, 0, 0.1)',
          padding: 24,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '14px',   // rounded-xl = --radius + 4px
          boxShadow: '0px 12px 24px 0px rgba(17,24,39,0.24)',
          background: '#ffffff',
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(3, 7, 18, 0.5)',
            backdropFilter: 'blur(8px)',
          },
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0px 8px 16px 0px rgba(17,24,39,0.12)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            height: 56,                  // h-14
            borderRadius: '10px',        // rounded-lg = --radius
            backgroundColor: '#E8EAED', // --input-surface
            transition: 'background-color 150ms ease, box-shadow 150ms ease',

            '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)': {
              padding: '0 16px',         // px-4, vertically centred by height
            },

            '& .MuiOutlinedInput-input': {
              fontSize: '1rem',
              color: '#374151',          // text-slate-700
              '&::placeholder': {
                color: '#4b5563',        // text-slate-500
                opacity: 1,
              },
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px #E8EAED inset',
                WebkitTextFillColor: '#374151',
                caretColor: '#374151',
                transition: 'background-color 5000s ease-in-out 0s',
              },
            },
            '& .MuiOutlinedInput-input.MuiSelect-select': {
              padding: '0 40px 0 16px',
            },

            // No border by default (border-0)
            '& fieldset.MuiOutlinedInput-notchedOutline': {
              border: 'none',
              borderRadius: '10px',
            },
            '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            // Focus: ring-2 ring-primary + white bg
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: '0 0 0 2px #094451',
            },
            '&.Mui-focused .MuiOutlinedInput-input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
              WebkitTextFillColor: '#374151',
            },
            '&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            // Error: ring-2 ring-destructive
            '&.Mui-error': {
              boxShadow: '0 0 0 2px #d4183d',
            },
            '&.Mui-error.Mui-focused': {
              boxShadow: '0 0 0 2px #d4183d',
            },
            '&.Mui-error fieldset.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            '&.Mui-disabled': {
              opacity: 0.5,
            },
          },
          '& .MuiFormHelperText-root': {
            margin: '6px 0 0',
            fontSize: '0.875rem',
            lineHeight: '22px',
            color: '#717182',
            position: 'inherit',
          },
          '& .MuiFormHelperText-root.Mui-error': {
            color: '#d4183d',
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#cbced4',          // --switch-background (unchecked track color)
          '&.Mui-checked': {
            color: '#1E95A0',        // --brand-cyan
          },
          '&.Mui-disabled': { opacity: 0.5 },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#cbced4',
          '&.Mui-checked': {
            color: '#1E95A0',
            '& .MuiSvgIcon-root:last-of-type': {
              transform: 'scale(0.8) !important',
            },
          },
        },
      },
    },

    MuiSwitch: {
      // Matches design guide: brand checked state (--brand-cyan), cbced4 unchecked
      styleOverrides: {
        root: {
          width: 36,
          height: 20,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 1,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#1E95A0',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
            },
            '&.Mui-disabled .MuiSwitch-thumb': { color: '#f3f3f5' },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
          },
          '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 14, height: 14 },
          '& .MuiSwitch-track': {
            borderRadius: '10px',
            backgroundColor: '#cbced4',
            opacity: 1,
            transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          '&.text-input-label': {
            paddingBottom: '8px',
          },
        }
      }
    },

    MuiAppBar: {
      // Matches app-bar.tsx: bgcolor="background.paper", borderBottom, color="text.primary"
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#111827',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          '&.MuiContainer-maxWidthLg': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1f2937',
          borderRadius: '6px',        // rounded-sm = --radius - 4px
          fontSize: '0.875rem',
          padding: '6px 10px',
        },
      },
    },
  },
});
